import { SearchOutlined } from '@ant-design/icons';
import {
  dirhams,
  findUuid,
  looksLikeShortId,
  type MonthKey,
  monthLabel,
  present,
  receiptNo,
  TYPE_LABELS,
} from '@horof/shared';
import { AutoComplete, Input, type InputRef } from 'antd';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from 'urql';
import { DeskFindDocument } from './find-data';

const NO_ID = '00000000-0000-0000-0000-000000000000';

/** One result, and where it opens. */
type Hit = {
  key: string;
  kind: string;
  label: ReactNode;
  to: string;
};

const month = (d: string) => monthLabel(d.slice(0, 7) as MonthKey);
/** A number inside an Arabic string, kept left to right. */
const ltr = (text: string) => `\u2066${text}\u2069`;

/**
 * The header's search (Ctrl+K, doc 56). It takes:
 * - an id, alone or in a copied link: every kind of record is tried;
 * - a receipt number «2026-00042»;
 * - a member's name, a meter number, a letter reference, a project or a
 *   counterparty.
 * A bill opens its page, a payment its drawer in «الأداءات» (doc 57).
 */
export function FindBox() {
  const navigate = useNavigate();
  const input = useRef<InputRef>(null);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');

  // Typing settles before asking arqam.
  useEffect(() => {
    const t = setTimeout(() => setQuery(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyK') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const id = findUuid(query);
  const receipt = id ? null : receiptNo(query);
  const byText = !id && !receipt && query.length >= 2;
  const [{ data, fetching }] = useQuery({
    query: DeskFindDocument,
    variables: {
      id: id ?? NO_ID,
      byId: !!id,
      text: query,
      byText,
      receiptNo: receipt ?? '',
      byReceipt: !!receipt,
    },
    pause: !id && !receipt && !byText,
  });

  const hits = useMemo((): Hit[] => {
    if (!data) return [];
    const out: Hit[] = [];
    const add = (h: Hit | false | null | undefined) => {
      if (h) out.push(h);
    };
    add(
      data.member && {
        key: data.member.id,
        kind: 'عضو',
        label: data.member.fullName,
        to: `/members/${data.member.id}`,
      },
    );
    add(
      data.meter && {
        key: data.meter.id,
        kind: 'عداد',
        label: `${data.meter.label} · ${data.meter.member?.fullName ?? ''}`,
        to: `/meters/${data.meter.id}`,
      },
    );
    add(
      data.bill && {
        key: data.bill.id,
        kind: 'فاتورة',
        label: `${month(data.bill.billMonth)} · ${data.bill.member?.fullName ?? ''}`,
        to: `/bills/${data.bill.id}`,
      },
    );
    for (const p of [data.payment, data.paymentByReceiptNo]) {
      add(
        p && {
          key: p.id,
          kind: 'أداء',
          label: `الوصل ${ltr(p.receiptNo)} · ${p.bill?.member?.fullName ?? ''}`,
          to: `/payments?panel=${p.id}`,
        },
      );
    }
    const op = data.financialOperation;
    add(
      op && {
        key: op.id,
        kind: 'عملية',
        label: `${TYPE_LABELS[op.type]} ${dirhams(op.amount)} · ${op.date} · ${op.description ?? ''}`,
        to: `/operations?panel=${op.id}`,
      },
    );
    const tr = data.accountTransfer;
    add(
      tr && {
        key: tr.id,
        kind: 'تحويل',
        label: `${dirhams(tr.amount)} · ${tr.date}`,
        to: `/transfers?panel=${tr.id}`,
      },
    );
    const debt = data.debt;
    add(
      debt && {
        key: debt.id,
        kind: 'قرض',
        label: debt.purpose ?? debt.counterparty?.name ?? '',
        to: `/debts?panel=${debt.id}`,
      },
    );
    const ik = data.inKindContribution;
    add(
      ik && {
        key: ik.id,
        kind: 'مساهمة عينية',
        label: ik.description,
        to: `/in-kind?panel=${ik.id}`,
      },
    );
    const doc = data.associationDocument;
    add(
      doc && {
        key: doc.id,
        kind: 'وثيقة',
        label: `${doc.reference ?? ''} ${doc.title}`,
        to: `/documents?panel=${doc.id}`,
      },
    );
    const st = data.bankStatement;
    add(
      st && {
        key: st.id,
        kind: 'كشف بنكي',
        label: `من ${st.periodStart} إلى ${st.periodEnd}`,
        to: `/bank/${st.id}`,
      },
    );
    const line = data.bankStatementLine;
    add(
      line && {
        key: line.id,
        kind: 'سطر كشف',
        label: `${line.lineNo}: ${line.label}`,
        to: `/bank/${line.statementId}?show=all&panel=${line.id}`,
      },
    );
    const named: [string, string, { id: string; name: string } | null][] = [
      ['مشروع', '/projects', data.project ?? null],
      ['جهة', '/counterparties', data.counterparty ?? null],
      ['حساب', '/accounts', data.financialAccount ?? null],
      ['صنف', '/categories', data.operationCategory ?? null],
    ];
    for (const [kind, path, x] of named) {
      add(
        x && {
          key: x.id,
          kind,
          label: x.name,
          to: `${path}?q=${encodeURIComponent(x.name)}`,
        },
      );
    }
    for (const m of present(data.members?.nodes)) {
      add({
        key: m.id,
        kind: 'عضو',
        label: m.fullName,
        to: `/members/${m.id}`,
      });
    }
    for (const m of present(data.meters?.nodes)) {
      add({
        key: m.id,
        kind: 'عداد',
        label: `${m.label} · ${m.member?.fullName ?? ''}`,
        to: `/meters/${m.id}`,
      });
    }
    for (const d of present(data.associationDocuments?.nodes)) {
      add({
        key: d.id,
        kind: 'وثيقة',
        label: `${d.reference ?? ''} ${d.title}`,
        to: `/documents?panel=${d.id}`,
      });
    }
    for (const p of present(data.projects?.nodes)) {
      add({
        key: p.id,
        kind: 'مشروع',
        label: p.name,
        to: `/projects?q=${encodeURIComponent(p.name)}`,
      });
    }
    for (const c of present(data.counterparties?.nodes)) {
      add({
        key: c.id,
        kind: 'جهة',
        label: c.name,
        to: `/counterparties?q=${encodeURIComponent(c.name)}`,
      });
    }
    return out;
  }, [data]);

  const open = (hit: Hit) => {
    setText('');
    input.current?.blur();
    if (hit.to) navigate(hit.to);
  };

  const notFound = !fetching && data && hits.length === 0;
  const hint = looksLikeShortId(query)
    ? 'هذا جزء من المعرّف: الصق المعرّف كاملاً.'
    : notFound
      ? id || receipt
        ? 'لا شيء بهذا المعرّف أو الرقم.'
        : 'لا نتيجة.'
      : null;

  return (
    <AutoComplete
      className="desk-find"
      value={text}
      onChange={setText}
      onSelect={(key: string) => {
        const hit = hits.find((h) => h.key === key);
        if (hit) open(hit);
      }}
      options={hits.map((h) => ({
        value: h.key,
        label: (
          <span className="desk-find-hit">
            <span className="desk-find-kind">{h.kind}</span>
            <span className="desk-find-label">{h.label}</span>
          </span>
        ),
      }))}
      notFoundContent={hint}
      // The text is not a filter: arqam has already chosen.
      filterOption={false}
    >
      <Input
        ref={input}
        allowClear
        prefix={<SearchOutlined />}
        placeholder="بحث أو لصق معرّف (Ctrl+K)"
        onPressEnter={() => {
          if (hits.length === 1) open(hits[0]);
        }}
      />
    </AutoComplete>
  );
}
